import React, { useState, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Proposal, ProposalState, getStateString, formatTimestamp } from '../contracts/contract';
import { ellipsizeCenter } from '../utils/stringUtils';
import './ProposalList.css';

interface ProposalListProps {
  proposals: Proposal[];
  onSelectProposal: (proposal: Proposal) => void;
  loading: boolean;
}

const ProposalList: React.FC<ProposalListProps> = ({ proposals, onSelectProposal, loading }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const proposalsPerPage = 10;

  // Sort proposals by voteStart timestamp (most recent first)
  const sortedProposals = useMemo(() => {
    return [...proposals].sort((a, b) => {
      const aTimestamp = Number(a.voteStart);
      const bTimestamp = Number(b.voteStart);
      return bTimestamp - aTimestamp; // Descending order (newest first)
    });
  }, [proposals]);

  // Calculate pagination
  const totalPages = Math.ceil(sortedProposals.length / proposalsPerPage);
  const startIndex = (currentPage - 1) * proposalsPerPage;
  const endIndex = startIndex + proposalsPerPage;
  const currentProposals = sortedProposals.slice(startIndex, endIndex);

  // Reset to page 1 when proposals change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [proposals.length]);

  const getStateClass = (state: ProposalState): string => {
    switch (state) {
      case ProposalState.Active:
        return 'state-active';
      case ProposalState.Succeeded:
        return 'state-succeeded';
      case ProposalState.Executed:
        return 'state-executed';
      case ProposalState.Defeated:
        return 'state-defeated';
      case ProposalState.Canceled:
        return 'state-canceled';
      case ProposalState.Expired:
        return 'state-expired';
      case ProposalState.Queued:
        return 'state-queued';
      default:
        return 'state-pending';
    }
  };

  const getVoteSummary = (proposal: Proposal): string => {
    const forVotes = Number(proposal.forVotes);
    const againstVotes = Number(proposal.againstVotes);
    const abstainVotes = Number(proposal.abstainVotes);
    const total = forVotes + againstVotes + abstainVotes;
    
    if (total === 0) return 'No votes yet';
    
    const forPercent = (forVotes / total * 100).toFixed(1);
    const againstPercent = (againstVotes / total * 100).toFixed(1);
    
    return `${forPercent}% for, ${againstPercent}% against`;
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Scroll to top of proposal list
    const proposalList = document.querySelector('.proposal-list');
    if (proposalList) {
      proposalList.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <div className="proposal-list">
        <div className="proposal-list-header">
          <h2>Proposals</h2>
        </div>
        <div className="loading">
          Loading proposals...
        </div>
      </div>
    );
  }

  if (proposals.length === 0) {
    return (
      <div className="proposal-list">
        <div className="proposal-list-header">
          <h2>Proposals</h2>
        </div>
        <div className="no-proposals">
          <p>No proposals found. Create the first proposal to get started!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="proposal-list">
      <div className="proposal-list-header">
        <h2>Proposals ({proposals.length})</h2>
      </div>
      <div className="proposals-grid">
        {currentProposals.map((proposal) => (
          <div 
            key={proposal.id} 
            className={`proposal-card ${getStateClass(proposal.state)}`}
            onClick={() => onSelectProposal(proposal)}
          >
            <div className="proposal-header">
              <div className="proposal-id" title={`Proposal ID: ${proposal.id}`}>
                #{ellipsizeCenter(proposal.id, 12)}
              </div>
              <div className={`proposal-state ${getStateClass(proposal.state)}`}>
                {getStateString(proposal.state)}
              </div>
            </div>
            
            <div className="proposal-content">
              <div className="proposal-description">
                {proposal.description ? (
                  <div className="markdown-content">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {proposal.description}
                    </ReactMarkdown>
                  </div>
                ) : (
                  'No description available'
                )}
              </div>
              
              <div className="proposal-details">
                <div className="proposal-proposer">
                  <strong>Proposer:</strong> {proposal.proposer ? `${proposal.proposer.slice(0, 6)}...${proposal.proposer.slice(-4)}` : 'Unknown'}
                </div>
                <div className="proposal-targets">
                  <strong>Targets:</strong> {proposal.targets.length} contract(s)
                </div>
                <div className="proposal-votes">
                  <strong>Votes:</strong> {getVoteSummary(proposal)}
                </div>
              </div>
            </div>
            
            <div className="proposal-footer">
              <div className="proposal-timestamps">
                {proposal.voteStart !== '0' && (
                  <div className="timestamp">
                    <strong>Vote Start:</strong> {formatTimestamp(proposal.voteStart)}
                  </div>
                )}
                {proposal.voteEnd !== '0' && (
                  <div className="timestamp">
                    <strong>Vote End:</strong> {formatTimestamp(proposal.voteEnd)}
                  </div>
                )}
              </div>
              
              <div className="proposal-actions">
                <button className="btn btn-primary btn-sm">
                  View Details
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <div className="pagination-info">
            Showing {startIndex + 1}-{Math.min(endIndex, sortedProposals.length)} of {sortedProposals.length} proposals
          </div>
          <div className="pagination-controls">
            <button
              className="pagination-btn"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              title="Previous page"
            >
              ‹
            </button>
            
            <div className="pagination-pages">
              {(() => {
                const pages = [];
                
                // Always show page 1
                pages.push(
                  <button
                    key={1}
                    className={`pagination-btn ${currentPage === 1 ? 'active' : ''}`}
                    onClick={() => handlePageChange(1)}
                    title="Page 1"
                  >
                    1
                  </button>
                );
                
                // Determine which pages to show around current page
                let startPage = Math.max(2, currentPage - 1);
                let endPage = Math.min(totalPages - 1, currentPage + 1);
                
                // Adjust range to show at most 3 consecutive pages
                if (endPage - startPage > 2) {
                  if (currentPage <= 3) {
                    endPage = startPage + 2;
                  } else {
                    startPage = endPage - 2;
                  }
                }
                
                // Add ellipsis after page 1 if there's a gap
                if (startPage > 2) {
                  pages.push(
                    <span key="ellipsis-start" className="pagination-ellipsis">...</span>
                  );
                }
                
                // Add pages around current page (excluding page 1 and last page)
                for (let page = startPage; page <= endPage; page++) {
                  if (page !== 1 && page !== totalPages) {
                    pages.push(
                      <button
                        key={page}
                        className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                        onClick={() => handlePageChange(page)}
                        title={`Page ${page}`}
                      >
                        {page}
                      </button>
                    );
                  }
                }
                
                // Add ellipsis before last page if there's a gap
                if (endPage < totalPages - 1) {
                  pages.push(
                    <span key="ellipsis-end" className="pagination-ellipsis">...</span>
                  );
                }
                
                // Always show last page (if it's not page 1)
                if (totalPages > 1) {
                  pages.push(
                    <button
                      key={totalPages}
                      className={`pagination-btn ${currentPage === totalPages ? 'active' : ''}`}
                      onClick={() => handlePageChange(totalPages)}
                      title={`Page ${totalPages}`}
                    >
                      {totalPages}
                    </button>
                  );
                }
                
                return pages;
              })()}
            </div>
            
            <button
              className="pagination-btn"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              title="Next page"
            >
              ›
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProposalList;
